import { useState, useMemo } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TextField, MenuItem, Select, FormControl, InputLabel, Grid, TableSortLabel
} from "@mui/material";
import mockData from "../../mocks/todas_evaluaciones.json";
import EvaluationDetailModal from "../TeamEvaluations/EvaluationDetailModal";

const scoreToQualitativeMap = {
  5: "1 - Excede ampliamente las expectativas",
  4: "2 - Desempeño por encima del esperado",
  3: "3 - Desempeño esperado",
  2: "4 - Desempeño por debajo del esperado",
  1: "5 - Bastante alejado del esperado"
};

const headCells = [
  { id: 'legajo', numeric: true, disablePadding: false, label: 'Nro Legajo' },
  { id: 'nombre', numeric: false, disablePadding: false, label: 'Nombre' },
  { id: 'apellido', numeric: false, disablePadding: false, label: 'Apellido' },
  { id: 'area', numeric: false, disablePadding: false, label: 'Área' },
  { id: 'lider', numeric: false, disablePadding: false, label: 'Líder' },
  { id: 'puesto', numeric: false, disablePadding: false, label: 'Puesto' },
  { id: 'puntaje', numeric: true, disablePadding: false, label: 'Puntaje' },
  { id: 'puntaje_calibrado', numeric: true, disablePadding: false, label: 'Puntaje Calibrado' },
  { id: 'fortalezas', numeric: false, disablePadding: false, label: 'Fortalezas' },
  { id: 'areas_mejora', numeric: false, disablePadding: false, label: 'Áreas de Mejora' },
];

const MAX_LINES = 2; // Define the maximum number of lines before truncating

export default function AllEvaluations() {
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const evaluations = mockData;
  const [expandedText, setExpandedText] = useState({}); // State to manage expanded text per row and field

  // Filtros
  const [filters, setFilters] = useState({
    area: '',
    lider: '',
    puntaje: '',
    puntaje_calibrado: '',
    nombre: '',
    apellido: '',
    legajo: ''
  });

  // Ordenamiento
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');

  // Opciones únicas para selects
  const areaOptions = useMemo(() => Array.from(new Set(evaluations.map(ev => ev.area))).sort(), [evaluations]);
  const liderOptions = useMemo(() => Array.from(new Set(evaluations.map(ev => ev.lider))).sort(), [evaluations]);
  const puntajeOptions = useMemo(() => Array.from(new Set(evaluations.map(ev => ev.puntaje))).sort((a, b) => b - a), [evaluations]);
  const puntajeCalibradoOptions = useMemo(() => Array.from(new Set(evaluations.map(ev => ev.puntaje_calibrado))).sort((a, b) => b - a), [evaluations]);

  // Filtrado y Ordenamiento
  const filteredAndSortedEvaluations = useMemo(() => {
    let filtered = evaluations.filter(ev => {
      if (filters.area && ev.area !== filters.area) return false;
      if (filters.lider && ev.lider !== filters.lider) return false;
      if (filters.puntaje && String(ev.puntaje) !== String(filters.puntaje)) return false;
      if (filters.puntaje_calibrado && String(ev.puntaje_calibrado) !== String(filters.puntaje_calibrado)) return false;
      if (filters.nombre && !ev.nombre.toLowerCase().includes(filters.nombre.toLowerCase())) return false;
      if (filters.apellido && !ev.apellido.toLowerCase().includes(filters.apellido.toLowerCase())) return false;
      if (filters.legajo && !ev.legajo.includes(filters.legajo)) return false;
      return true;
    });

    if (orderBy) {
      filtered.sort((a, b) => {
        let valA = a[orderBy];
        let valB = b[orderBy];

        // Handle numeric values for sorting puntaje and legajo
        if (orderBy === 'legajo' || orderBy === 'puntaje' || orderBy === 'puntaje_calibrado') {
          valA = parseInt(valA);
          valB = parseInt(valB);
        }

        if (valA < valB) {
          return order === 'asc' ? -1 : 1;
        } else if (valA > valB) {
          return order === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [evaluations, filters, order, orderBy]);

  const handleFilterChange = (field) => (event) => {
    setFilters(f => ({ ...f, [field]: event.target.value }));
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const toggleExpandedText = (legajo, field) => {
    setExpandedText(prev => ({
      ...prev,
      [legajo]: {
        ...prev[legajo],
        [field]: !prev[legajo]?.[field]
      }
    }));
  };

  const TextWithToggle = ({ text, legajo, field }) => {
    const isExpanded = expandedText[legajo]?.[field];
    const lines = text.split('\n').filter(line => line.trim() !== ''); // Split by newline and filter empty lines

    if (lines.length <= MAX_LINES && text.length < 150) { // Also consider character length for single long lines
      return <Box sx={{ whiteSpace: 'pre-line' }}>{text}</Box>;
    }

    const displayedText = isExpanded ? text : lines.slice(0, MAX_LINES).join('\n') + '...';

    return (
      <Box>
        <Typography component="span" sx={{ whiteSpace: 'pre-line' }}>{displayedText}</Typography>
        <Button size="small" onClick={() => toggleExpandedText(legajo, field)} sx={{ textTransform: 'none' }}>
          {isExpanded ? 'Ver menos' : 'Ver más'}
        </Button>
      </Box>
    );
  };

  return (
    <Box p={4}>
      <Typography variant="h4" mb={4}>Todas las Evaluaciones</Typography>
      <Box mb={2} display="flex" flexWrap="wrap" gap={2}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Área</InputLabel>
              <Select
                label="Área"
                value={filters.area}
                onChange={handleFilterChange('area')}
              >
                <MenuItem value="">Todas</MenuItem>
                {areaOptions.map(area => (
                  <MenuItem key={area} value={area}>{area}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Líder</InputLabel>
              <Select
                label="Líder"
                value={filters.lider}
                onChange={handleFilterChange('lider')}
              >
                <MenuItem value="">Todos</MenuItem>
                {liderOptions.map(lider => (
                  <MenuItem key={lider} value={lider}>{lider}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Puntaje</InputLabel>
              <Select
                label="Puntaje"
                value={filters.puntaje}
                onChange={handleFilterChange('puntaje')}
              >
                <MenuItem value="">Todos</MenuItem>
                {puntajeOptions.map(p => (
                  <MenuItem key={p} value={p}>{scoreToQualitativeMap[p] || p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Puntaje Calibrado</InputLabel>
              <Select
                label="Puntaje Calibrado"
                value={filters.puntaje_calibrado}
                onChange={handleFilterChange('puntaje_calibrado')}
              >
                <MenuItem value="">Todos</MenuItem>
                {puntajeCalibradoOptions.map(p => (
                  <MenuItem key={p} value={p}>{scoreToQualitativeMap[p] || p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Nombre"
              size="small"
              value={filters.nombre}
              onChange={handleFilterChange('nombre')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Apellido"
              size="small"
              value={filters.apellido}
              onChange={handleFilterChange('apellido')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Legajo"
              size="small"
              value={filters.legajo}
              onChange={handleFilterChange('legajo')}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  padding={headCell.disablePadding ? 'none' : 'normal'}
                  sortDirection={orderBy === headCell.id ? order : false}
                  sx={{ fontWeight: 'bold', color: '#1976d2' }}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 'bold', color: '#1976d2' }}></TableCell> {/* Empty cell for 'Ver detalle' button */}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedEvaluations.map((ev) => {
              return (
                <TableRow key={ev.legajo}>
                  <TableCell>{ev.legajo}</TableCell>
                  <TableCell>{ev.nombre}</TableCell>
                  <TableCell>{ev.apellido}</TableCell>
                  <TableCell>{ev.area}</TableCell>
                  <TableCell>{ev.lider}</TableCell>
                  <TableCell>{ev.puesto}</TableCell>
                  <TableCell>{scoreToQualitativeMap[ev.puntaje] || ev.puntaje}</TableCell>
                  <TableCell>{scoreToQualitativeMap[ev.puntaje_calibrado] || ev.puntaje_calibrado}</TableCell>
                  <TableCell>
                    <TextWithToggle text={ev.fortalezas.join('\n')} legajo={ev.legajo} field="fortalezas" />
                  </TableCell>
                  <TableCell>
                    <TextWithToggle text={ev.areas_mejora.join('\n')} legajo={ev.legajo} field="areas_mejora" />
                  </TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => setSelectedEvaluation(ev)}>
                      Ver detalle
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <EvaluationDetailModal
        open={!!selectedEvaluation}
        onClose={() => setSelectedEvaluation(null)}
        evaluation={selectedEvaluation}
      />
    </Box>
  );
} 